import { action, computed, makeObservable, observable } from "mobx";
import { RootStore } from "src/stores/RootStore";
import { IAccountingsHistoryByAccountNumber, IAccountingsHistoryTag } from "./InvoiceTypes";
import { GET_ACCOUNTINGS_HISTORY, GET_ACCOUNTINGS_HISTORY_BY_ACCOUNT_NUMBER } from "src/api/cred";
import { apolloClientInstance } from "src/network/apolloClientInstance";
import { GetAccountingsHistory, GetAccountingsHistoryVariables } from "src/api/generated/GetAccountingsHistory";
import {
    GetAccountingsHistoryByAccountNumber,
    GetAccountingsHistoryByAccountNumberVariables
} from "src/api/generated/GetAccountingsHistoryByAccountNumber";

export enum STATE {
    INIT,
    LOADING,
    LOADING_HISTORY_TAG_DATA
}

export const MAX_ACCOUNTING_HISTORY_TAGS_TO_SHOW = 3;

export class AccountingBookingTextStore {
    rootStore: RootStore;

    state: STATE = STATE.INIT;

    // Request cache for GET_ACCOUNTINGS_HISTORY, keyed by creditor + realestate (the only query
    // variables). Every accounting row with the same creditor/realestate shares ONE request instead
    // of firing its own; in-flight requests are shared too, so N rows initialised at once still
    // produce a single round trip per distinct key. Deliberately NOT observable: it is plumbing.
    private accountingsHistoryRequests: Map<string, Promise<IAccountingsHistoryTag[]>> = new Map();
    // Monotonic counter per row so a slow, older request can never overwrite a newer result.
    private accountingsHistoryRequestSequence: number[] = [];

    accountingsHistoryTagData: Array<IAccountingsHistoryTag[]> = [];
    areAllAccountingsHistoryTagsDisplayed: boolean[] = [];
    wasAccountingsHistoryTagSelected: boolean[] = [];

    isBookingTextsOfAccountModalDisplayed: boolean = false;
    bookingTextOfAccountsModalSearchQuery: string = "";
    accountingsHistoryByAccountNumberData: IAccountingsHistoryByAccountNumber[] = [];
    isLoadingAccountingsHistoryByAccountNumber: boolean = false;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;

        makeObservable(this, {
            state: observable,
            accountingsHistoryTagData: observable,
            areAllAccountingsHistoryTagsDisplayed: observable,
            wasAccountingsHistoryTagSelected: observable,
            isBookingTextsOfAccountModalDisplayed: observable,
            bookingTextOfAccountsModalSearchQuery: observable,
            accountingsHistoryByAccountNumberData: observable,
            isLoadingAccountingsHistoryByAccountNumber: observable,

            setState: action,
            reset: action,
            removeAccountingRow: action,
            loadAccountingsHistoryTagData: action,
            setAccountingsHistoryTagData: action,
            setAreAllAccountingsHistoryTagsDisplayed: action,
            setWasAccountingsHistoryTagSelected: action,
            setIsBookingTextsOfAccountModalDisplayed: action,
            setBookingTextOfAccountsModalSearchQuery: action,
            setIsLoadingAccountingsHistoryByAccountNumber: action,
            setAccountingsHistoryByAccountNumberData: action,
            loadAccountingsHistoryDataByAccountNumber: action,

            displayedAccountingsHistoryTagsByIndex: computed
        });
    }

    setState = (state: STATE) => {
        this.state = state;
    };

    // Called when a (different) invoice is initialised: per-row tag state and the request cache
    // belong to the invoice's creditor/realestates and must not leak into the next invoice.
    reset = () => {
        this.accountingsHistoryTagData = [];
        this.areAllAccountingsHistoryTagsDisplayed = [];
        this.wasAccountingsHistoryTagSelected = [];
        this.accountingsHistoryRequests.clear();
        this.accountingsHistoryRequestSequence = [];
    };

    /*  See removeAccountingRowFromSearchStores: the per row values have to be removed and not just
        cleared, otherwise they stay attached to the index of the row that was deleted.
    */
    removeAccountingRow = (accountingRowIndex: number) => {
        this.accountingsHistoryTagData.splice(accountingRowIndex, 1);
        this.areAllAccountingsHistoryTagsDisplayed.splice(accountingRowIndex, 1);
        this.wasAccountingsHistoryTagSelected.splice(accountingRowIndex, 1);
        this.accountingsHistoryRequestSequence.splice(accountingRowIndex, 1);
    };

    setIsBookingTextsOfAccountModalDisplayed = (isDisplayed: boolean) => {
        this.isBookingTextsOfAccountModalDisplayed = isDisplayed;
    };

    setBookingTextOfAccountsModalSearchQuery = (searchQuery: string) => {
        this.bookingTextOfAccountsModalSearchQuery = searchQuery;
    };

    setAccountingsHistoryTagData = (
        accountingsHistoryTagData: IAccountingsHistoryTag[],
        accountingRowIndex: number
    ) => {
        this.accountingsHistoryTagData[accountingRowIndex] = accountingsHistoryTagData;
    };

    setAccountingsHistoryByAccountNumberData = (
        accountingsHistoryByAccountNumberData: IAccountingsHistoryByAccountNumber[]
    ) => {
        this.accountingsHistoryByAccountNumberData = accountingsHistoryByAccountNumberData;
    };

    setAreAllAccountingsHistoryTagsDisplayed = (
        accountingRowIndex: number,
        areAllAccountingsHistoryTagsDisplayed: boolean
    ) => {
        this.areAllAccountingsHistoryTagsDisplayed[accountingRowIndex] = areAllAccountingsHistoryTagsDisplayed;
    };

    setWasAccountingsHistoryTagSelected = (accountingRowIndex: number, wasSelected: boolean) => {
        this.wasAccountingsHistoryTagSelected[accountingRowIndex] = wasSelected;
    };

    loadAccountingsHistoryTagData = async (
        accountingRowIndex: number,
        creditorId: string,
        realestateId: string,
        accountNumber?: string
    ) => {
        const sequence = (this.accountingsHistoryRequestSequence[accountingRowIndex] ?? 0) + 1;
        this.accountingsHistoryRequestSequence[accountingRowIndex] = sequence;

        const accountingsHistory = await this.fetchAccountingsHistory(creditorId, realestateId || "");

        // A newer request for this row has been issued in the meantime - its result wins.
        if (this.accountingsHistoryRequestSequence[accountingRowIndex] !== sequence) {
            return;
        }

        const seenTexts = new Set<string>();

        // The account filter is applied per row on the shared result: the query itself only knows
        // creditor and realestate (it does not declare an accountNumber variable).
        const accountingsHistoryTagData: IAccountingsHistoryTag[] = accountingsHistory.filter(
            (accountingsHistoryEntry) => {
                if (accountNumber && accountingsHistoryEntry.accountNumber !== accountNumber) {
                    return false;
                }
                // Skip duplicates
                if (seenTexts.has(accountingsHistoryEntry.text)) {
                    return false;
                }
                seenTexts.add(accountingsHistoryEntry.text);
                return true;
            }
        );

        this.setAccountingsHistoryTagData(accountingsHistoryTagData, accountingRowIndex);
    };

    private fetchAccountingsHistory = (creditorId: string, realestateId: string): Promise<IAccountingsHistoryTag[]> => {
        const cacheKey = `${creditorId}|${realestateId}`;

        const pendingRequest = this.accountingsHistoryRequests.get(cacheKey);
        if (pendingRequest) {
            return pendingRequest;
        }

        const request = apolloClientInstance
            .query<GetAccountingsHistory, GetAccountingsHistoryVariables>({
                query: GET_ACCOUNTINGS_HISTORY,
                variables: {
                    creditorId: creditorId,
                    realestateId: realestateId
                }
            })
            .then(({ data }) =>
                data.accountings_history.map((accountingsHistoryEntry) => {
                    return {
                        creditorId: accountingsHistoryEntry.creditorid,
                        text: accountingsHistoryEntry.text,
                        accountNumber: accountingsHistoryEntry.accountnumber,
                        system: accountingsHistoryEntry.system
                    };
                })
            )
            .catch((error) => {
                // Do not cache failures - the next caller gets a fresh attempt.
                this.accountingsHistoryRequests.delete(cacheKey);
                throw error;
            });

        this.accountingsHistoryRequests.set(cacheKey, request);

        return request;
    };

    loadAccountingsHistoryDataByAccountNumber = async (accountingRowIndex: number) => {
        this.setIsLoadingAccountingsHistoryByAccountNumber(true);

        const currentAccount = this.rootStore.invoiceStore.currentInvoice?.getAccountingAccount(accountingRowIndex);

        if (currentAccount && currentAccount.number) {
            const { data }: { data: GetAccountingsHistoryByAccountNumber } = await apolloClientInstance.query<
                GetAccountingsHistoryByAccountNumber,
                GetAccountingsHistoryByAccountNumberVariables
            >({
                query: GET_ACCOUNTINGS_HISTORY_BY_ACCOUNT_NUMBER,
                variables: {
                    accountNumber: currentAccount.number
                }
            });
            const seenTexts = new Set<string>();

            const accountingsHistoryByAccountNumberData: IAccountingsHistoryByAccountNumber[] = data.accountings_history
                .filter((accountingsHistoryEntry) => {
                    // Skip duplicates
                    if (seenTexts.has(accountingsHistoryEntry.text)) {
                        return false;
                    }
                    seenTexts.add(accountingsHistoryEntry.text);
                    return true;
                })
                .map((accountingsHistoryEntry) => {
                    const creditorName = accountingsHistoryEntry.creditor?.name || "-";
                    return {
                        text: accountingsHistoryEntry.text,
                        accountNumber: accountingsHistoryEntry.accountnumber,
                        creditorName: creditorName
                    };
                });

            this.setAccountingsHistoryByAccountNumberData(accountingsHistoryByAccountNumberData);
        }

        this.setIsLoadingAccountingsHistoryByAccountNumber(false);
    };

    setIsLoadingAccountingsHistoryByAccountNumber = (isLoading: boolean) => {
        this.isLoadingAccountingsHistoryByAccountNumber = isLoading;
    };

    getAccountingsHistoryTagsCount = (accountingRowIndex: number): number => {
        if (this.accountingsHistoryTagData[accountingRowIndex]) {
            return this.accountingsHistoryTagData[accountingRowIndex].length;
        } else {
            return 0;
        }
    };

    hasMoreAccountingsHistoryTagsToDisplay = (accountingRowIndex: number): boolean => {
        return (
            this.getAccountingsHistoryTagsCount(accountingRowIndex) > MAX_ACCOUNTING_HISTORY_TAGS_TO_SHOW &&
            (this.areAllAccountingsHistoryTagsDisplayed[accountingRowIndex] === undefined ||
                this.areAllAccountingsHistoryTagsDisplayed[accountingRowIndex] === false)
        );
    };

    get displayedAccountingsHistoryTagsByIndex(): IAccountingsHistoryTag[][] {
        return this.accountingsHistoryTagData.map((accountingsHistoryTags, index) => {
            const allAccountingsHistoryTagsAreDisplayed = this.areAllAccountingsHistoryTagsDisplayed[index] === true;
            if (
                allAccountingsHistoryTagsAreDisplayed ||
                this.getAccountingsHistoryTagsCount(index) <= MAX_ACCOUNTING_HISTORY_TAGS_TO_SHOW
            ) {
                return accountingsHistoryTags;
            } else if (this.hasMoreAccountingsHistoryTagsToDisplay(index)) {
                return accountingsHistoryTags.slice(0, MAX_ACCOUNTING_HISTORY_TAGS_TO_SHOW);
            }
            return [];
        });
    }

    get filteredAccountingsHistoryByAccountNumberData(): IAccountingsHistoryByAccountNumber[] {
        const searchQuery = this.bookingTextOfAccountsModalSearchQuery.toLowerCase();

        if (!searchQuery) {
            return this.accountingsHistoryByAccountNumberData;
        }

        return this.accountingsHistoryByAccountNumberData.filter(
            (entry) =>
                entry.text.toLowerCase().includes(searchQuery) || entry.creditorName.toLowerCase().includes(searchQuery)
        );
    }
}
